import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

interface DragOrigin {
  pointerId: number;
  startX: number;
  startY: number;
  origX: number;
  origY: number;
}

/**
 * Pointer-drag offset for the Talk to Heidi dock. Resets whenever the dock
 * closes so a session dragged into a corner does not reopen off-screen.
 */
export function useVoiceDockDrag(isOpen: boolean) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<DragOrigin | null>(null);

  useEffect(() => {
    if (!isOpen) setOffset({ x: 0, y: 0 });
  }, [isOpen]);

  const onPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origX: offset.x,
      origY: offset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setOffset({
      x: drag.origX + (event.clientX - drag.startX),
      y: drag.origY + (event.clientY - drag.startY),
    });
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // already released
    }
  };

  return { offset, onPointerDown, onPointerMove, onPointerUp };
}
